/**
 * Traduction du panier RESTO de la popup en charge utile d'API.
 *
 * La forme produite est EXACTEMENT `purchase.foods.v3` du parcours client
 * (/api/v1/payment/add-to-basket) : c'est elle que le serveur sait déjà stocker
 * (FoodService::storeFoodsWithPacksV3). Le devis n'invente donc aucun format —
 * un plat chiffré sur un devis et le même plat commandé produisent les mêmes
 * lignes.
 *
 * Module séparé, sans dépendance à Vue : la mise en forme se teste seule, et le
 * composant n'a plus qu'à afficher.
 *
 * ⚠️ Frontend = zéro confiance. On n'envoie QUE des identifiants et des
 * quantités. Les prix affichés dans la popup restent à l'écran : le serveur
 * relit tous les tarifs en base, et rien ici ne peut influencer un montant.
 */

// Sur place. Le devis ne propose ni vente à emporter ni livraison — ces modes
// supposent une date de retrait/livraison que le devis ne collecte pas.
const FOOD_TYPE_ON_SITE = 0

function positiveQuantity(value) {
    const quantity = parseInt(value, 10)
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1
}

/**
 * Options de carte (sauce, cuisson, taille) retenues sur un plat.
 * Le serveur les retrouve par le couple (attribut, valeur).
 */
function toOptions(selections) {
    return (selections || [])
        .filter((selection) => selection.kind === 'option' && selection.group_id && selection.id)
        .map((selection) => ({
            attributeId: selection.group_id,
            valueId: selection.id,
            quantity: positiveQuantity(selection.quantity),
        }))
}

/**
 * Suppléments retenus sur un plat. Seul l'item compte côté serveur : son groupe
 * ne sert qu'à l'affichage et aux bornes min/max, déjà appliquées dans la popup.
 */
function toAddons(selections) {
    return (selections || [])
        .filter((selection) => selection.kind === 'addon' && selection.id)
        .map((selection) => ({
            itemId: selection.id,
            quantity: positiveQuantity(selection.quantity),
        }))
}

function buildFoodLine(uuid, quantity, selections) {
    return {
        uuid,
        quantity: positiveQuantity(quantity),
        food_type: FOOD_TYPE_ON_SITE,
        options: toOptions(selections),
        addons: toAddons(selections),
    }
}

/**
 * Un menu : un niveau = un produit retenu, qui emporte sa propre configuration
 * dans `extras`. Les niveaux sans produit retenu sont absents de la sélection —
 * la popup ne laisse pas valider tant qu'il en reste un à choisir.
 */
function buildPackLine(uuid, quantity, selections) {
    const items = (selections || [])
        .filter((selection) => selection.kind === 'pack' && selection.uuid)
        .map((selection) => ({
            name: selection.group_name || '',
            foods: [
                {
                    uuid: selection.uuid,
                    // Un niveau de menu ne contient qu'UN exemplaire du produit : sa
                    // multiplication suit la quantité du menu, côté serveur.
                    quantity: 1,
                    checked: true,
                    options: toOptions(selection.extras),
                    addons: toAddons(selection.extras),
                },
            ],
        }))

    return {
        uuid,
        quantity: positiveQuantity(quantity),
        food_type: FOOD_TYPE_ON_SITE,
        items,
    }
}

/**
 * Construit la charge utile à partir des entrées du panier resto.
 *
 * Une entrée regroupe un article par uuid : sa `quantity` est le total affiché
 * sur la carte, et `lines` détaille CHAQUE configuration validée dans la popup.
 * Deux burgers aux suppléments différents sont deux lignes distinctes — les
 * fusionner facturerait les suppléments de l'un sur la quantité des deux.
 *
 * Le reliquat (`quantity` non couverte par les `lines`) correspond aux ajouts
 * directs, sans popup : ils partent en ligne nue, sans option ni supplément.
 *
 * @param {Array} entries panier resto (StepItemSelection)
 * @returns {{foods: Array, packs: Array}}
 */
export function buildFoodQuotePayload(entries) {
    const foods = []
    const packs = []

    ;(entries || []).forEach((entry) => {
        if (!entry || !entry.uuid) return

        const lines = entry.lines || []

        lines.forEach((line) => {
            if (line.is_pack) {
                packs.push(buildPackLine(entry.uuid, line.quantity, line.options))
                return
            }
            foods.push(buildFoodLine(entry.uuid, line.quantity, line.options))
        })

        const configuredQuantity = lines.reduce((sum, line) => sum + positiveQuantity(line.quantity), 0)
        const plainQuantity = positiveQuantity(entry.quantity) - configuredQuantity

        // Un menu passe TOUJOURS par la popup : un reliquat sur un menu signifierait
        // une composition inconnue, on ne l'invente pas.
        if (plainQuantity > 0 && !entry.is_pack) {
            foods.push(buildFoodLine(entry.uuid, plainQuantity, []))
        }
    })

    return { foods, packs }
}

export default buildFoodQuotePayload
